import { MultiVehicleDealResource } from "./dealsheet/MultiVehicleDealResource";

export type IDealsheetData = {
  offerId: string;
  dealType: string;
  behaviorDrivers: Array<IBehaviorDriver>;
  customer: IDealsheetCustomer;
  currentVehicle: IVehicle;
  primaryReplacementVehicle: IVehicle;
  secondaryReplacementVehicle: IVehicle;
  bps: ISectionBps;
  inMarket: ISectionInMarket;
  vehicle: ISectionVehicle;
};

export type IDealsheetCustomer = {
  name: string;
  phone: string;
};

export type IVehicle = {
  vehicleStyle: string;
  trim: string;
};

export type IBehaviorDriver = {
  name: string;
  path: string;
  value: number;
};

export type ISectionBps = {
  good: Array<ITalkingPointItem>;
  neutral: Array<ITalkingPointItem>;
  avoid: Array<ITalkingPointItem>;
};

export type ITalkingPointItem = {
  type: string;
  value: number;
  content: string;
};

export type ISectionInMarket = {
  remainingMonths: string;
  remainingMiles: string;
  currentEstimatedMileage: string;
  currentEstimatedOverMileage: string;
  currentEstimatedPenalty: string;
  totalAllowedMileage: string;
  projectedLeaseEndMileage: string;
  estimatedOverMileage: string;
  estimatedLeaseEndPenalty: string;
  anticipatedVehicleReturnDate: string;
  warrantyYears: string;
  warrantyMiles: string;
  warrantyStatus: string;
  warrantyRemainingMonths: string;
  warrantyRemainingMiles: string;
};

export type ISectionVehicle = {
  replacementOptions: Array<IVehicleOption>;
  competitiveConsideration: Array<IVehicleOption>;
};

export type IVehicleOption = {
  type: string;
  vehicleYear: string;
  vehicleModel: string;
  vehicleTrim: string;
  msrp: string;
  vin: string;
  score: string;
  imageSrc: string;
  isSelected: string;
};

export enum IBpsScoreType {
  BPS = 'Bps',
  IN_MARKET = 'In Market',
  VEHICLE = 'Vehicle',
  DEAL = 'Deal',
}

export enum IDealsheetTab {
  OFFER = 'Offer',
  SHOWROOM = 'Showroom',
  BEHAVIOR_DRIVERS = 'Behavior Drivers',
}

export type ILegacyDealsheetData = {
  dealerId: string;
  closedDealId: string;
};

export type IContractModalData = {
  id?: string | null;
  version?: any | null;
  amountFinanced?: number | null;
  assignedToUserId?: number | null;
  assignedToUserName?: string | null;
  balloonAmount?: number | null;
  bankName?: string | null;
  buyerFullName?: string | null;
  cashPrice?: number | null;
  chromeId?: number | null;
  amVehicleId?: string | null;
  closedDealId?: string | null;
  confirmContract?: boolean | null;
  dealDate?: string | null;
  deliveryMileage?: number | null;
  downPayment?: number | null;
  driveOffAmount?: number | null;
  equifaxConfirmationNeeded?: boolean | null;
  endDate?: string | null;
  hasExtendedWarranty?: boolean | null;
  isBalloonDeal?: boolean | null;
  isNew?: boolean | null;
  isPaidOff?: boolean | null;
  leaseAnnualMiles?: number | null;
  msrp?: number | null;
  netCapCost?: number | null;
  payment?: number | null;
  rate?: number | null;
  residual?: number | null;
  soldBy?: string | null;
  term?: number | null;
  totalCapReduction?: number | null;
  transUnionConfirmationNeeded?: boolean | null;
  vehicleExtColor?: string | null;
  vehicleStyle?: string | null;
  vin?: string | null;
  warrantyDate?: string | null;
  warrantyMiles?: number | null;
  dealType?: string | null;
  amVehicleStyleExists?: boolean | null;
  year?: string | null;
  model?: string | null;
  trim?: string | null;
  imageUrl?: string | null;
  isContractExpired?: boolean | null;
  vehicleTypes?: { name: string; value: boolean }[] | null;
  vehicleType?: { name: string; value: boolean } | null;
};

export const contractTypeMap = {
  id: 'string',
  version: 'any',
  amountFinanced: 'number',
  assignedToUserId: 'number',
  AssignedToUserName: 'string',
  balloonAmount: 'number',
  bankName: 'string',
  buyerFullName: 'string',
  cashPrice: 'number',
  chromeId: 'number',
  amVehicleId: 'string',
  closedDealId: 'string',
  confirmContract: 'boolean',
  dealDate: 'string',
  deliveryMileage: 'number',
  downPayment: 'number',
  driveOffAmount: 'number',
  equifaxConfirmationNeeded: 'boolean',
  endDate: 'string',
  hasExtendedWarranty: 'boolean',
  isBalloonDeal: 'boolean',
  isNew: 'boolean',
  isPaidOff: 'boolean',
  leaseAnnualMiles: 'number',
  msrp: 'number',
  netCapCost: 'number',
  payment: 'number',
  rate: 'number',
  residual: 'number',
  term: 'number',
  totalCapReduction: 'number',
  transUnionConfirmationNeeded: 'boolean',
  vehicleExtColor: 'string',
  vehicleStyle: 'string',
  vin: 'string',
  warrantyDate: 'string',
  warrantyMiles: 'number',
  dealType: 'string',
  amVehicleStyleExists: 'boolean',
  year: 'string',
  model: 'string',
  trim: 'string',
  imageUrl: 'string',
  isContractExpired: 'boolean',
  vehicleTypes: 'object',
  vehicleType: 'object',
  soldBy: 'string',
};

type ModelDetails = {
  name: string;
};

type FmcVehicle = {
  amVehicleIds: string[] | null;
  modelCodes: string[] | null;
  modelsDetails: ModelDetails[] | null;
};

export type FmcPrivateIncentive = {
  source: string;
  amId: string;
  amName: string;
  oemId: string;
  oemName: string;
  approvalDate: string;
  approverName: string;
  startDate: string;
  endDate: string;
  eligibleDealTypes: any;
  redemptionCode: string;
  campaignType: null;
  term: any;
  moneyDown: any;
  mileage: any;
  offerRateType: any;
  excluded: FmcVehicle | null;
  offeredVehicles: FmcVehicle[];
  privateIncentiveAmount: number;
  replacementVehicles: FmcVehicle[];
  incentives: any;
};

export type MultiVehicleComparisonData = {
  Replacement: {
    Lease: MultiVehicleDealResource[],
    Finance: MultiVehicleDealResource[],
    Cash: MultiVehicleDealResource[]
  },
  Competitive: {
    Lease: MultiVehicleDealResource[],
    Finance: MultiVehicleDealResource[],
    Cash: MultiVehicleDealResource[]
  },
};

export type MultiVehicleComparisonOverrides = {
  Primary: {
    leaseOverrides: null,
    financeOverrides: null,
    cashOverrides: null
  },
  Secondary: {
    leaseOverrides: null,
    financeOverrides: null,
    cashOverrides: null
  },
  Temporary: {
    leaseOverrides: null,
    financeOverrides: null,
    cashOverrides: null
  },
  Turn: {
    leaseOverrides: null,
    financeOverrides: null,
    cashOverrides: null
  }
};
